<template>
  <div>
    <div class="harbor-details__section">
      <h2 class="harbor-details__title">{{ group.name }}</h2>
      <p v-if="group.purpose" class="harbor-details__purpose">{{ group.purpose }}</p>
      <div v-if="syncedRosterCount > 0" class="harbor-details__sync">
        <button v-if="admin"
                type="button"
                class="harbor-sync-chip"
                data-cy="sync-chip"
                :aria-label="`${syncedRosterCount} synced ${syncedRosterCount === 1 ? 'roster' : 'rosters'}, open sync settings`"
                @click="$emit('sync-click')">
          <i class="fa-solid fa-arrows-rotate" aria-hidden="true"></i>
          {{ syncedRosterCount }} synced {{ syncedRosterCount === 1 ? 'roster' : 'rosters' }}
        </button>
        <span v-else class="harbor-sync-chip harbor-sync-chip--static">
          <i class="fa-solid fa-arrows-rotate" aria-hidden="true"></i>
          {{ syncedRosterCount }} synced {{ syncedRosterCount === 1 ? 'roster' : 'rosters' }}
        </span>
      </div>
    </div>

    <div class="harbor-details__section">
      <p class="harbor-details__label">Members</p>
      <template v-if="showMemberCount">
        <div class="flex align-items-center justify-space-between m-b-xs">
          <button type="button"
                  class="chat-details__member-toggle text-14 font-semi text-gray-900"
                  :aria-expanded="showMemberPanel ? 'true' : 'false'"
                  aria-controls="chat-details-member-panel"
                  @click="toggleShowMembers">
            {{ memberCount }} members
            <i :class="showMemberPanel ? 'fa-solid fa-caret-down' : 'fa-solid fa-caret-right'" aria-hidden="true"></i>
          </button>
          <VDropdown theme="info-tooltip">
            <i class="fa-solid fa-circle-question text-gray-500 cursor-pointer"></i>
            <template v-slot:popper>
              <div>
                <p class="header">What is an 'inactive' member?</p>
                <p>
                  Inactive members do not receive any notifications.
                  Inactive members are people who have a profile in WrestlingIQ,
                  but have either never been invited or have never accepted their invite.
                </p>
              </div>
            </template>
          </VDropdown>
        </div>
      </template>
      <template v-else>
        <div class="loading-inline">
          <small>Loading members...</small>
          <spinner></spinner>
        </div>
      </template>
      <div id="chat-details-member-panel" v-show="showMemberPanel">
        <paged-list response-key="memberships" object-type="MessageMembership" :base-url="memberUrl"
                    :manual-paging="true">
          <template v-slot:list="slotProps">
            <div class="flex gap-x-xxs align-items-center">
              <VDropdown theme="info-tooltip" v-if="admin && slotProps.result.muted">
                <svg xmlns="http://www.w3.org/2000/svg" viewBox="0 0 640 512" class="w-4 h-4 color-gray-700" fill="currentColor">
                  <path d="M633.99 471.02L36 3.51C29.1-2.01 19.03-.9 13.51 6l-10 12.49C-2.02 25.39-.9 35.46 6 40.98l598 467.51c6.9 5.52 16.96 4.4 22.49-2.49l10-12.49c5.52-6.9 4.41-16.97-2.5-22.49zM163.53 368c16.71-22.03 34.48-55.8 41.4-110.58l-45.47-35.55c-3.27 90.73-36.47 120.68-54.84 140.42-6 6.45-8.66 14.16-8.61 21.71.11 16.4 12.98 32 32.1 32h279.66l-61.4-48H163.53zM320 96c61.86 0 112 50.14 112 112 0 .2-.06.38-.06.58.02 16.84 1.16 31.77 2.79 45.73l59.53 46.54c-8.31-22.13-14.34-51.49-14.34-92.85 0-77.7-54.48-139.9-127.94-155.16V32c0-17.67-14.32-32-31.98-32s-31.98 14.33-31.98 32v20.84c-26.02 5.41-49.45 16.94-69.13 32.72l38.17 29.84C275 103.18 296.65 96 320 96zm0 416c35.32 0 63.97-28.65 63.97-64H256.03c0 35.35 28.65 64 63.97 64z"/>
                </svg>
                <template v-slot:popper>
                  <div>
                    <p class="header">Muted</p>
                    <p>This user has muted this group.</p>
                  </div>
                </template>
              </VDropdown>
              <p class="p-h-xxs m-b-none">
                <a v-if="isLinkableMember(slotProps)"
                   :href="urlForProfile(slotProps.result.profile)"
                   :class="memberClass(slotProps)">
                  {{ memberLabel(slotProps) }}
                </a>
                <span v-else :class="memberClass(slotProps)">
                  {{ memberLabel(slotProps) }}
                </span>
              </p>
            </div>
          </template>
        </paged-list>
      </div>
      <small v-if="canChangeMembers">
        <a :href="editMembersLink">Edit members...</a>
      </small>
    </div>
  </div>
</template>

<script>
import Spinner from './spinner.vue';
import PagedList from './paged_list.vue';

export default {
  name: 'chat-details',
  components: { Spinner, PagedList },
  props: {
    group: { type: Object, required: true },
    admin: { type: Boolean, default: false },
    memberCount: { type: Number, default: 0 },
    showMemberCount: { type: Boolean, default: false },
    memberUrl: { type: String, required: true },
  },
  data() {
    return {
      showMemberPanel: false,
    };
  },
  computed: {
    canChangeMembers() {
      return this.admin;
    },
    editMembersLink() {
      return `/message_groups/${this.group.id}/edit_memberships`;
    },
    syncedRosterCount() {
      return (this.group.message_group_roster_syncers || []).length;
    },
  },
  methods: {
    toggleShowMembers() {
      this.showMemberPanel = !this.showMemberPanel;
    },
    isLinkableMember(props) {
      return this.admin && props.result.profile.profile_type !== 'Customer';
    },
    memberLabel(props) {
      const name = props.result.profile.display_name;
      const status = props.result.profile.user_status;
      return status === 'active' ? name : `${name} (${status})`;
    },
    memberClass(props) {
      return props.result.profile.user_status === 'active' ? '' : 'text-muted';
    },
    urlForProfile(profile) {
      const type = profile.profile_type;
      const pId = profile.profile_id;
      if (type === 'ParentProfile') return `/parents/${pId}`;
      if (type === 'CoachProfile') return `/coaches/${pId}`;
      if (type === 'WrestlerProfile') return `/wrestlers/${pId}`;
      return '';
    },
  },
};
</script>
